import React, { useState, useEffect, useCallback } from "react";
import {
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCircle,
} from "react-icons/fa";
import { Link } from "react-router-dom";
const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;
const BALANCE_API = `https://${process.env.REACT_APP_API_KEY_AUTH_BALANCE}.execute-api.ap-south-1.amazonaws.com/default/balanceUpdate`;

const SelfTransactionManager = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSelfWaiting = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const all = data.transactions || [];

      // Filter: From and To are same, Status is WAITING
      const filtered = all.filter(
        (tx) => tx.fromBranch === tx.toBranch && tx.status === "WAITING",
      );

      setTransactions(
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
      );
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSelfWaiting();
  }, [fetchSelfWaiting]);

  // --- REBUILD LOGIC (Strict Independence) ---
  const rebuildBranchBalance = async (targetId) => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const allTransactions = data.transactions || [];

      const masterCalculatedBalance = allTransactions.reduce((acc, tx) => {
        // 1. HARD FILTER: Ignore Rejected or Split-Parent transactions
        if (tx.status === "REJECTED" || tx.status === "SPLIT") return acc;
        if (tx.status === "PENDING" && tx.flowRole === "RECEIVER") {
          return acc;
        }
        if (tx.status === "WAITING" && tx.flowRole === "RECEIVER") {
          return acc;
        }

        const amt = Number(tx.amount || 0);
        const comm = Number(tx.commission || 0);

        // --- RULE: ONLY process if this specific record belongs to the targetId ---
        const isMySenderRecord =
          tx.fromBranch === targetId && tx.flowRole === "SENDER";
        const isMyReceiverRecord =
          tx.toBranch === targetId && tx.flowRole === "RECEIVER";

        if (isMySenderRecord) {
          // --- SENDER AMOUNT LOGIC ---
          if (["IN", "BTIN", "SPLITIN", "BTSPLITIN"].includes(tx.paymentType)) {
            acc += amt;
          } else {
            acc -= amt;
          }

          // --- SENDER COMMISSION LOGIC ---
          // Only add commission if it exists on THIS specific sender record
          acc += comm;
        } else if (isMyReceiverRecord) {
          // --- RECEIVER AMOUNT LOGIC ---
          // Only add/reduce balance if not pending
          if (tx.status !== "PENDING" && tx.status !== "WAITING") {
            if (["IN", "BTIN", "SPLITIN", "BTSPLITIN"].includes(tx.paymentType)) {
              acc += amt;
            } else {
              acc -= amt;
            }
          }

          // --- RECEIVER COMMISSION LOGIC ---
          // Only add commission if it exists on THIS specific receiver record
          // (In your screenshot, this is 0 for BLR, so it adds nothing. Correct!)
          acc += comm;
        }

        return acc;
      }, 0);

      // --- DATABASE SYNC ---
      const balRes = await fetch(
        `${BALANCE_API}?id=${targetId}&branchName=${targetId}`,
      );
      const balData = await balRes.json();
      const currentDB = Number(balData.currentBalance || 0);
      const adjustment = masterCalculatedBalance - currentDB;

      if (Math.abs(adjustment) < 0.01) return;

      await fetch(BALANCE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: targetId,
          branchName: targetId,
          amount: Math.abs(adjustment),
          action: adjustment > 0 ? "ADD" : "REDUCE",
        }),
      });

      console.log(
        `Sync for ${targetId} complete. Master Balance: ${masterCalculatedBalance}`,
      );
    } catch (err) {
      console.error("Rebuild Sync Failed:", err);
    }
  };

  const handleUpdateStatus = async (tx, newStatus) => {
    setLoading(true);
    try {
      await fetch(API_URL, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentId: tx.paymentId,
          paymentType: tx.paymentType,
          status: newStatus,
          remarks: `Admin Approved via Self Request on ${new Date().toLocaleString()}`,
        }),
      });

      // Recalculate the branch balance after the status change
      await rebuildBranchBalance(tx.fromBranch);

      alert(`Transaction ${newStatus} successfully.`);
      fetchSelfWaiting();
    } catch (err) {
      alert("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2>
          <FaUserCircle /> Self-Transaction Manager
        </h2>
        <Link to="/">
          <button className="Branch-nav">Back to Admin</button>
        </Link>
        <button onClick={fetchSelfWaiting} className="br-btn br-btn-primary">
          <FaSyncAlt className={loading ? "spin" : ""} /> Refresh
        </button>
      </div>

      <div className="table-responsive">
        <table className="branch-table">
          <thead>
            <tr>
              <th>Branch</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Comm</th>
              <th>Date</th>
              <th>Status</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length > 0 ? (
              transactions.map((tx) => (
                <tr key={tx.paymentId}>
                  <td>
                    <strong>{tx.fromBranch}</strong>
                  </td>
                  <td>{tx.paymentType}</td>
                  <td>₹{tx.amount.toLocaleString()}</td>
                  <td>₹{tx.commission || 0}</td>
                  <td>{tx.timestamp?.split("T")[0]}</td>
                  <td>{tx.remarks}</td>
                  <td>
                    <span className="status-pill waiting">{tx.status}</span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleUpdateStatus(tx, "DELIVERED")}
                      style={{
                        background: "#22c55e",
                        color: "white",
                        border: "none",
                        padding: "5px 10px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "5px",
                      }}
                    >
                      <FaCheckCircle /> Deliver
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(tx, "REJECTED")}
                      style={{
                        background: "#ef4444",
                        color: "white",
                        border: "none",
                        padding: "5px 10px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <FaTimesCircle /> Reject
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No self-transactions in WAITING status.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SelfTransactionManager;
